import type { components } from "@libs/ticketing-client";
import type { Appearance, CssFontSource } from "@stripe/stripe-js";

export type BrandingTokens = components["schemas"]["BuyerCheckoutBranding"];
export type FontKey = NonNullable<BrandingTokens["font_key"]>;

/** No organizer branding: Lissen's neutral defaults throughout. */
export const NO_BRANDING: BrandingTokens = {
  logo_url: null,
  brand_color: null,
  accent_color: null,
  background_color: null,
  text_color: null,
  font_key: null,
  corner_radius: null,
};

type ResolvedBranding = {
  brand_color: string;
  accent_color: string;
  background_color: string;
  text_color: string;
  font_key: FontKey;
  corner_radius: number;
};

/** An organizer with no branding of its own gets Lissen's light look, the
 *  one the tickets sheet wears: white, near-black text, the Lissen blue. */
const DEFAULTS = {
  brand_color: "#0082ec",
  accent_color: "#6e6e73",
  background_color: "#ffffff",
  text_color: "#1d1d1f",
  font_key: "system" as const,
  corner_radius: 12,
};

const fontFamily: Record<FontKey, string> = {
  inter: 'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
  system: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  manrope: 'Manrope, system-ui, -apple-system, "Segoe UI", sans-serif',
};

const fontCss: Record<Exclude<FontKey, "system">, string> = {
  inter:
    "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap",
  manrope:
    "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&display=swap",
};

/** The stack a curated font key names. Exported so the drop-in component's
 *  appearance can fall back to it when a partner names no family of its own. */
export function fontFamilyFor(key: FontKey) {
  return fontFamily[key];
}

export function resolvedTokens(tokens: BrandingTokens): ResolvedBranding {
  return {
    brand_color: tokens.brand_color ?? DEFAULTS.brand_color,
    accent_color: tokens.accent_color ?? DEFAULTS.accent_color,
    background_color: tokens.background_color ?? DEFAULTS.background_color,
    text_color: tokens.text_color ?? DEFAULTS.text_color,
    font_key: tokens.font_key ?? DEFAULTS.font_key,
    corner_radius: tokens.corner_radius ?? DEFAULTS.corner_radius,
  };
}

/** The page consumes these variables; organizer input never becomes CSS. */
export function tokensToCssVariables(tokens: BrandingTokens) {
  const resolved = resolvedTokens(tokens);
  return {
    "--checkout-brand-color": resolved.brand_color,
    "--checkout-accent-color": resolved.accent_color,
    "--checkout-background-color": resolved.background_color,
    "--checkout-text-color": resolved.text_color,
    "--checkout-font-family": fontFamily[resolved.font_key],
    "--checkout-corner-radius": `${resolved.corner_radius}px`,
  };
}

export type CheckoutAppearance = {
  appearance: Appearance;
  fonts: Array<CssFontSource>;
};

/** Build Stripe's appearance and the page variables from the same token set. */
export function tokensToAppearance(tokens: BrandingTokens): CheckoutAppearance {
  const resolved = resolvedTokens(tokens);
  const family = fontFamily[resolved.font_key];
  const fonts =
    resolved.font_key === "system"
      ? []
      : [{ cssSrc: fontCss[resolved.font_key] }];

  return {
    appearance: {
      theme: "stripe",
      variables: {
        colorPrimary: resolved.brand_color,
        colorBackground: resolved.background_color,
        colorText: resolved.text_color,
        colorTextSecondary: resolved.accent_color,
        borderRadius: `${resolved.corner_radius}px`,
        fontFamily: family,
      },
    },
    fonts,
  };
}

export function fontStylesheet(tokens: BrandingTokens) {
  const key = resolvedTokens(tokens).font_key;
  return key === "system" ? null : fontCss[key];
}
